// src/components/analytics/AnalyticsSettings.jsx
import React, { useMemo, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import MonthRangePicker from './MonthRangePicker';
import MonthPicker from './MonthPicker';
import { analyticsAPI } from '../../services/api';

export const ANALYSIS = {
  TOTAL_BY_CLIENT: 'Monthly Total Insects Trapped',
  CAPTURE_TREND_BY_AREA: 'Monthly Capture Trend Analysis',
  SPECIES_IDENTIFIED_BY_LOCATION: 'Species Identified Analysis of The Month',
  SPECIES_TREND_BY_LOCATION: 'Monthly Species Trend Analysis',
};

export default function AnalyticsSettings({
  value,
  onChange,
  onGenerate,
  onDownload,
  errorMsg,
  canDownload = false,
  exporting = false,
  canGenerate = true,
}) {
  const [filterOptions, setFilterOptions] = useState({
    globals: [],
    regions: [],
    clients: [],
    addresses: [],
    locations: [],
    devices: [],
    species: []
  });

  const {
    type, global, region, client, address, location, device, species, threshold, range
  } = value;

  // Fetch filter options based on current selections (cascading)
  useEffect(() => {
    async function fetchOptions() {
      try {
        const response = await analyticsAPI.getFilterOptions({
          global: global || undefined,
          region: region || undefined,
          client: client || undefined,
          address: address || undefined,
          location: location || undefined
        });
        setFilterOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    }
    fetchOptions();
  }, [global, region, client, address, location]);

  // Which fields to show per analysis type
  const fields = useMemo(() => {
    switch (type) {
      case ANALYSIS.TOTAL_BY_CLIENT:
        return { global: true, region: true, client: true, address: true, location: true, device: true, threshold: false, species: true, range: true };
      case ANALYSIS.CAPTURE_TREND_BY_AREA:
        return { global: true, region: true, client: true, address: true, location: true, device: true, threshold: true, species: true, range: true };
      case ANALYSIS.SPECIES_IDENTIFIED_BY_LOCATION:
        return { global: true, region: true, client: true, address: true, location: true, device: true, threshold: false, species: true, range: true };
      case ANALYSIS.SPECIES_TREND_BY_LOCATION:
        return { global: true, region: true, client: true, address: true, location: true, device: true, threshold: false, species: true, range: true };
      default:
        return { global: false, region: false, client: false, address: false, location: false, device: false, threshold: false, species: false, range: false };
    }
  }, [type]);

  const handleTypeChange = (e) => {
    const nextType = e.target.value;
    // Only update the type, keep all other fields intact
    onChange({ type: nextType });
  };

  // Cascading handlers - clear dependent fields when parent changes
  const handleGlobalChange = (value) => {
    onChange({ 
      global: value,
      region: '',
      client: '',
      address: '',
      location: '',
      device: ''
    });
  };

  const handleRegionChange = (value) => {
    onChange({ 
      region: value,
      client: '',
      address: '',
      location: '',
      device: ''
    });
  };

  const handleClientChange = (value) => {
    onChange({ 
      client: value,
      address: '',
      location: '',
      device: ''
    });
  };

  const handleAddressChange = (value) => {
    onChange({ 
      address: value,
      location: '',
      device: ''
    });
  };

  const handleLocationChange = (value) => {
    onChange({ 
      location: value,
      device: ''
    });
  };

  const showMore = Boolean(type);

  return (
    <section className="anl-section anl-settings">
      {/* ROW 1 — Type of Analysis */}
      <div className="anl-row">
        <div className="anl-col span-12">
          <label className="anl-label sr-only">Type of Analysis</label>
          <select
            className={`anl-input anl-type-select ${type ? 'has-value' : ''}`}
            value={type}
            onChange={handleTypeChange}
          >
            <option value="">Select Type of Analysis</option>
            <option value={ANALYSIS.TOTAL_BY_CLIENT}>{ANALYSIS.TOTAL_BY_CLIENT}</option>
            <option value={ANALYSIS.CAPTURE_TREND_BY_AREA}>{ANALYSIS.CAPTURE_TREND_BY_AREA}</option>
            <option value={ANALYSIS.SPECIES_IDENTIFIED_BY_LOCATION}>{ANALYSIS.SPECIES_IDENTIFIED_BY_LOCATION}</option>
            <option value={ANALYSIS.SPECIES_TREND_BY_LOCATION}>{ANALYSIS.SPECIES_TREND_BY_LOCATION}</option>
          </select>
        </div>
      </div>

      {/* Only show the rest when a type is picked */}
      {showMore && (
        <>
          {/* ROW 2 — Global | Region | Client */}
          <div className="anl-row">
            <div className="anl-col span-4">
              {fields.global ? (
                <div className="anl-inline">
                  <span className="anl-label">Global:</span>
                  <select
                    className="anl-input"
                    value={global}
                    onChange={(e) => handleGlobalChange(e.target.value)}
                  >
                    <option value="">Select Global</option>
                    {filterOptions.globals.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ) : <div className="anl-slot-empty" />}
            </div>

            <div className="anl-col span-4">
              {fields.region ? (
                <div className="anl-inline">
                  <span className="anl-label">Region:</span>
                  <select
                    className="anl-input"
                    value={region}
                    onChange={(e) => handleRegionChange(e.target.value)}
                    disabled={!global}
                  >
                    <option value="">Select Region</option>
                    {filterOptions.regions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              ) : <div className="anl-slot-empty" />}
            </div>

            <div className="anl-col span-4">
              {fields.client ? (
                <div className="anl-inline">
                  <span className="anl-label">Client:</span>
                  <select
                    className="anl-input"
                    value={client}
                    onChange={(e) => handleClientChange(e.target.value)}
                    disabled={!global || !region}
                  >
                    <option value="">Select Client</option>
                    {filterOptions.clients.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ) : <div className="anl-slot-empty" />}
            </div>
          </div>

          {/* ROW 3 — Address | Location | Device */}
          <div className="anl-row">
            <div className="anl-col span-4">
              {fields.address ? (
                <div className="anl-inline">
                  <span className="anl-label">Address:</span>
                  <select
                    className="anl-input"
                    value={address}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    disabled={!global || !region || !client}
                  >
                    <option value="">Select Address</option>
                    {filterOptions.addresses.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              ) : <div className="anl-slot-empty" />}
            </div>

            <div className="anl-col span-4">
              {fields.location ? (
                <div className="anl-inline">
                  <span className="anl-label">Location:</span>
                  <select
                    className="anl-input"
                    value={location}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    disabled={!global || !region || !client || !address}
                  >
                    <option value="">Select Location</option>
                    {filterOptions.locations.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              ) : <div className="anl-slot-empty" />}
            </div>

            <div className="anl-col span-4">
              {fields.device ? (
                <div className="anl-inline">
                  <span className="anl-label">Device:</span>
                  <select
                    className="anl-input"
                    value={device}
                    onChange={(e) => onChange({ device: e.target.value })}
                    disabled={!global || !region || !client || !address || !location}
                  >
                    <option value="">Select Device</option>
                    {(filterOptions.devices || []).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              ) : <div className="anl-slot-empty" />}
            </div>
          </div>

          {/* ROW 4 — Species/Threshold | Range | Actions */}
          <div className="anl-row">
            <div className="anl-col span-4">
              {fields.threshold ? (
                <div className="anl-inline">
                  <span className="anl-label">Threshold:</span>
                  <input
                    className="anl-input"
                    type="number"
                    placeholder="Input Threshold"
                    value={threshold}
                    onChange={(e) => onChange({ threshold: e.target.value })}
                  />
                </div>
              ) : fields.species ? (
                <div className="anl-inline">
                  <span className="anl-label">Species:</span>
                  <select
                    className="anl-input"
                    value={species}
                    onChange={(e) => onChange({ species: e.target.value })}
                  >
                    <option value="">Select Species</option>
                    {filterOptions.species.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              ) : (
                <div className="anl-slot-empty" />
              )}
            </div>

            <div className="anl-col span-4">
              {fields.range ? (
                <div className="anl-inline anl-range-single">
                  <span className="anl-label">Range:</span>
                  {type === ANALYSIS.SPECIES_IDENTIFIED_BY_LOCATION ? (
                    <MonthPicker
                      value={range?.from || ''}
                      onChange={(ym) => onChange({ range: ym ? { from: ym, to: ym } : null })}
                      placeholder="MM/YYYY"
                    />
                  ) : (
                    <MonthRangePicker
                      value={range}
                      onChange={(v) => onChange({ range: v })}
                      placeholder="MM/YYYY – MM/YYYY"
                    />
                  )}
                </div>
              ) : <div className="anl-slot-empty" />}
            </div>

            <div className="anl-col span-4">
              <div className="anl-actions">
                <button
                  type="button"
                  className="anl-btn-primary"
                  onClick={onGenerate}
                  disabled={!canGenerate}
                  title={canGenerate ? 'Generate analysis' : 'Select a Type first'}
                >
                  GENERATE
                </button>
                <button
                  type="button"
                  className="anl-btn-download"
                  onClick={onDownload}
                  disabled={!canDownload || exporting}
                  aria-label={exporting ? 'Exporting...' : 'Download PDF'}
                  title={exporting ? 'Exporting PDF...' : canDownload ? 'Download PDF' : 'Generate analysis first'}
                >
                  <FontAwesomeIcon 
                    icon={faDownload} 
                    spin={exporting}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Inline validation error */}
          {errorMsg ? (
            <div className="anl-error" role="alert" aria-live="polite">
              {errorMsg}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}